import "./index.scss";

import { Link, useLocation } from "react-router-dom";
import PropTypes from "prop-types";

import { fetchImage, goBack, getSearchParams } from "../../nonUIFuncs";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLocationDot } from "@fortawesome/free-solid-svg-icons";

/**
 * Individual navigation button component for the top navigation bar.
 * Renders a Link with specific styling based on the icon type.
 *
 * @param {Object} props - Component props
 * @param {string} props.pathname - The path to navigate to when clicked
 * @param {string} props.icon - Icon identifier used for CSS class naming
 * @param {string} props.search - URL search parameters to include
 * @returns {JSX.Element} A styled navigation button link
 */
const TopNavigationButton = ({ pathname, icon, search }) => {
  return (
    <Link
      to={{
        pathname: pathname,
        search: search,
      }}
      className={`topnavigation_button_container_${icon}`}
      aria-label={icon === "home" ? "Home" : icon === "back" ? "Go back" : icon}
    />
  );
};

/**
 * Top navigation bar component that displays navigation controls and unit information.
 * Conditionally renders based on showNav prop and includes home, back, menu, and exit buttons.
 * Also displays unit badges and headers when navigating within specific game units.
 *
 * @param {Object} props - Component props
 * @param {boolean} props.showNav - Whether to show the navigation bar
 * @param {boolean} props.showClearData - Current state of clear data popup visibility
 * @param {Function} props.setShowClearData - Function to toggle clear data popup
 * @param {Function} props.setShowMenu - Function to toggle navigation menu
 * @returns {JSX.Element|null} The top navigation component or null if hidden
 */
const TopNavigation = ({
  showNav,
  showClearData,
  setShowClearData,
  setShowMenu,
  config,
}) => {
  const routeLocation = useLocation();
  const params = getSearchParams(routeLocation);
  const unitParam = params.find((d) => d.id === "unit");
  const difficultyParam = params.find((d) => d.id === "difficulty");
  const locationParam = params.find((d) => d.id === "location")?.value;
  let fixSearchForStoryUnit = routeLocation.search.split("&");

  if (routeLocation.search.includes("difficulty")) {
    let difficulty = fixSearchForStoryUnit
      .find((d) => d.includes("difficulty"))
      .split("?")[1];
    fixSearchForStoryUnit = `?&${difficulty}`;
  }
  let badgeImage = "";
  if (unitParam !== undefined) {
    badgeImage = `url(${fetchImage(
      `Common_Category_Badge_${unitParam.modifier}1.png`,
    )})`;
    if (unitParam.value.toUpperCase().includes("STORY")) {
      badgeImage = `url(${fetchImage(
        `Common_Category_Badge_Story_${unitParam.modifier}.png`,
      )})`;
    }
  }
  if (showNav === true) {
    return (
      <div className="topnavigation_container">
        <div className="topnavigation_backgroundimage" />
        <div className="topnavigation_contents">
          <TopNavigationButton pathname="/" icon="home" search="" />

          {/* <div onClick={setShowMenu}>Show Menu</div> */}

          <TopNavigationButton
            pathname={goBack(routeLocation).pathname}
            icon="back"
            search={goBack(routeLocation).search}
          />
          <div
            className={`topnavigation_button_container_menu`}
            onClick={setShowMenu}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                setShowMenu();
              }
            }}
            role="button"
            tabIndex={0}
            aria-label="Open menu"
          />
          {/* {reduxState.historyData !== undefined ? (
            <TopNavigationButton
              pathname="/timeline"
              icon="timeline"
              search=""
            />
          ) : null} */}
          {/* <TopNavigationButton
            pathname="/endless-mode"
            icon="endless"
            search=""
          /> */}
          {/* {config.Credits !== undefined ? (
            <TopNavigationButton pathname="/credits" icon="credits" search="" />
          ) : null} */}
          {/* <TopNavigationButton pathname="/shoot-me" icon="plus" search="" />
          <TopNavigationButton pathname="/" icon="pause" search="" /> */}
          {config?.hasLocationSelection && locationParam && localStorage.getItem("kz_location") && (
            <Link
              to={`/choose-location?difficulty=${difficultyParam?.value}&change=1`}
              className="topnavigation_button_container_location hovercursor"
              aria-label={`Change location: ${locationParam}`}
            >
              <span className="topnavigation_location_name">{locationParam}</span>
              <FontAwesomeIcon icon={faLocationDot} />
            </Link>
          )}
          <div
            className="topnavigation_button_container_exit"
            onClick={() => setShowClearData(!showClearData)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                setShowClearData(!showClearData);
              }
            }}
            role="button"
            tabIndex={0}
            aria-label="Exit and manage data"
          />
          {/* <Link
            to={{
              pathname: "/animation-test",
            }}
          >
            <div
              style={{
                padding: 10,
                backgroundColor: "cornflowerblue",
                color: "white",
                fontWeight: "bold",
              }}
            >
              Follow straight line animation
            </div>
          </Link> */}
        </div>
        {unitParam?.value.length > 0 ? (
          <div className="topnavigation_headercontainer">
            <h1 className="topnavigation_header">{unitParam.value}</h1>
          </div>
        ) : null}
        {unitParam?.value.length > 0 ? (
          <div className="topnavigation_badgecontainer">
            <div
              className="topnavigation_badge"
              style={{
                backgroundImage: badgeImage,
              }}
            />
          </div>
        ) : null}
      </div>
    );
  } else {
    return null;
  }
};

export default TopNavigation;
export { TopNavigationButton };

TopNavigationButton.propTypes = {
  pathname: PropTypes.string,
  icon: PropTypes.string,
  search: PropTypes.string,
};

TopNavigation.propTypes = {
  showNav: PropTypes.bool,
  showClearData: PropTypes.bool,
  setShowClearData: PropTypes.func,
  config: PropTypes.object,
};
